// UserHashtagCard.jsx
import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import WithIntersectionObserver from './WithIntersectionObserver.jsx';
import UniversalContainer from './UniversalContainer.jsx';
import UserStatusIndicator from '../container/UserStatusIndicator.js';
import media from '../style/media.js';
import { textColor, color } from '../style/variables.js';
import TextEllipsis from '../style/TextEllipsis.js';
import trackMixpanel, {
  ButtonId,
  EventTypes,
  getViewId,
} from '../resource/mixpanel.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

export const UserHashtagCard = ({
  userId,
  username,
  hashtags,
  hashtagFeedUser,
  shouldShowUserHashtags,
  rank,
  shouldShowRank,
}) => {
  const { t } = useTranslation();
  return (
    <StyledUserHashtagDetail>
      <WithIntersectionObserver key={userId} shouldKeepObserve>
        {({ isIntersecting }) => (
          <LiveStreamCardWrapper>
            <UniversalContainer
              filename="LiveStreamCard"
              userId={userId}
              streamId={userId}
              isIntersecting={isIntersecting}
              isOnUserGrid={true}
            />
            <MetaData>
              <UsernameLink to={`/user/${userId}`}>
                {shouldShowRank && <Rank>{rank}</Rank>}
                <Username>@{username}</Username>
                <UserStatusIndicatorWrapper>
                  <UserStatusIndicator
                    userId={userId}
                    livestreamSize={12}
                    shouldShowLiveStream
                    shouldShowOnline
                  />
                </UserStatusIndicatorWrapper>
              </UsernameLink>
              {shouldShowUserHashtags && (
                <Hashtags>
                  {hashtags.slice(0, 2).map((tag, index) => {
                    const feedName = hashtagFeedUser.replace('{HASHTAG}', tag);
                    return (
                      <HashtagLink
                        key={index}
                        to={`/user-feed-grid/${feedName}`}
                        onClick={() => {
                          trackMixpanel({
                            type: EventTypes.USER_CLICKED,
                            payload: {
                              viewId: getViewId({
                                pathname: window.location.pathname,
                              }),
                              buttonId: ButtonId.Hashtag.ButtonUserHashtag,
                              extraProps: {
                                hashtag: tag,
                              },
                            },
                          });
                        }}
                      >
                        {t(`user_hashtag_${tag}`, {
                          ns: TranslationNamespace.FEED,
                        })}
                      </HashtagLink>
                    );
                  })}
                </Hashtags>
              )}
            </MetaData>
          </LiveStreamCardWrapper>
        )}
      </WithIntersectionObserver>
    </StyledUserHashtagDetail>
  );
};

UserHashtagCard.propTypes = {
  userId: PropTypes.string,
  username: PropTypes.string,
  hashtagFeedUser: PropTypes.string,
  rank: PropTypes.number,
  hashtags: PropTypes.array,
  shouldShowUserHashtags: PropTypes.bool,
  shouldShowRank: PropTypes.bool,
};

UserHashtagCard.defaultProps = {
  userId: '',
  username: '',
  hashtags: [],
  hashtagFeedUser: '',
  rank: null,
  shouldShowUserHashtags: false,
  shouldShowRank: false,
};

const StyledUserHashtagDetail = styled.div`
  width: 100%;
  height: 100%;
`;

const LiveStreamCardWrapper = styled.article``;

const MetaData = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 4px 6px 8px 6px;
`;

const UsernameLink = styled(Link)`
  display: flex;
  align-items: flex-end;
  font-size: 16px;
  font-weight: 600;
  line-height: 150%;
  letter-spacing: 0.024px;

  ${media.tablet`
    font-size: 14px;
  `}
`;

const Username = styled.span`
  ${TextEllipsis};
`;

const Rank = styled.span`
  margin-right: 2px;
  flex: none;
  font-size: 32px;
  font-weight: 600;
  line-height: 1;
  color: ${color.yellow};
  ::after {
    content: '/';
    font-size: 12px;
    line-height: 1.5;
  }
`;

const UserStatusIndicatorWrapper = styled.span`
  margin-left: 2px;
  height: 24px; // should be as same as UsernameLink 16px * 1.5
  flex: none;
  display: inline-flex;
  align-items: center;
  ${media.mobile`
    height: 21px; // should be as same as UsernameLink 14px * 1.5
  `};
`;

const Hashtags = styled.div`
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  display: -webkit-box;
`;

const HashtagLink = styled(Link)`
  margin-right: 4px;
  display: inline-block;
  border-radius: 40px;
  padding: 2px 8px;
  background-color: rgba(221, 221, 221, 0.2);
  color: ${textColor.primary};
  line-height: 150%; /* 24px */
  letter-spacing: 0.024px;
  font-size: 16px;
  ${media.tablet`
    font-size: 12px;
  `};
`;

export default UserHashtagCard;
